import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { filterCanceledUsers } from 'lib/util'
import { SiteUserTable } from './component.styles'
import {
  NAME,
  ELEMENTARY_COMPLETION,
  ELEMENTARY_TRAINING,
  EXPERIENCE,
  HIGH_SCHOOL_TRAINING,
  MIDDLE_SCHOOL_COMPLETION,
  MIDDLE_SCHOOL_TRAINING,
  TEACHES_LESSONS,
  TOTAL_ELEM_TRAININGS,
  TOTAL_MS_TRAININGS,
  TOTAL_DWB_TRAININGS,
  DWB_COLUMN_WIDTHS,
  VARIANT_K8_CORE,
  VARIANT_K12_DWB,
} from './constants'
import { coreCompletionFraction, dwbTrainingCompletionStatus } from './helpers'

const teachesLessonsDefined = row =>
  row?.flatPreferences?.teachesLessons !== undefined

const SiteUserTrainingGrid = ({ className, users, variant }) => {
  const columns = useMemo(() => {
    if (variant === VARIANT_K12_DWB) {
      const dwbCell = count =>
        dwbTrainingCompletionStatus(count, TOTAL_DWB_TRAININGS)

      const dwbSort = count => {
        if (count === null) {
          return -1
        }
        return count ?? 0
      }

      return [
        {
          title: NAME,
          fieldValue: row => {
            return <b>{`${row.firstName} ${row.lastName}`}</b>
          },
          sortValue: row => row.firstName,
        },
        {
          title: ELEMENTARY_TRAINING,
          fieldValue: row => dwbCell(row?.trainingCompletion?.dwb?.elem),
          unbold: true,
          sortValue: row => dwbSort(row?.trainingCompletion?.dwb?.elem),
        },
        {
          title: MIDDLE_SCHOOL_TRAINING,
          fieldValue: row => dwbCell(row?.trainingCompletion?.dwb?.ms),
          unbold: true,
          sortValue: row => dwbSort(row?.trainingCompletion?.dwb?.ms),
        },
        {
          title: HIGH_SCHOOL_TRAINING,
          fieldValue: row => dwbCell(row?.trainingCompletion?.dwb?.hs),
          unbold: true,
          sortValue: row => dwbSort(row?.trainingCompletion?.dwb?.hs),
        },
      ]
    }

    return [
      {
        title: NAME,
        fieldValue: row => {
          return <b>{`${row.firstName} ${row.lastName}`}</b>
        },
        sortValue: row => row.firstName,
      },
      {
        title: TEACHES_LESSONS,
        fieldValue: row =>
          row?.flatPreferences?.teachesLessons
            ? 'Yes'
            : row?.flatPreferences?.teachesLessons === undefined
            ? 'No role set'
            : 'No',
        unbold: true,
        sortValue: row =>
          row?.flatPreferences?.teachesLessons
            ? 1
            : row?.flatPreferences?.teachesLessons === undefined
            ? 3
            : 2,
      },
      {
        title: EXPERIENCE,
        fieldValue: row => row?.flatPreferences?.experience || '—',
        unbold: true,
      },
      {
        title: ELEMENTARY_COMPLETION,
        fieldValue: row =>
          coreCompletionFraction(
            row?.trainingCompletion?.elem,
            TOTAL_ELEM_TRAININGS,
            teachesLessonsDefined(row),
          ),
        unbold: true,
      },
      {
        title: MIDDLE_SCHOOL_COMPLETION,
        fieldValue: row =>
          coreCompletionFraction(
            row?.trainingCompletion?.ms,
            TOTAL_MS_TRAININGS,
            teachesLessonsDefined(row),
          ),
        unbold: true,
      },
    ]
  }, [variant])

  const defaultSort =
    variant === VARIANT_K12_DWB
      ? { columnTitle: NAME, direction: 'asc' }
      : {
          columnTitle: TEACHES_LESSONS,
          direction: 'asc',
        }

  const numOfColumns = variant === VARIANT_K12_DWB ? 4 : 7

  return (
    <SiteUserTable
      className={className}
      columnWidths={variant === VARIANT_K12_DWB ? DWB_COLUMN_WIDTHS : undefined}
      columns={columns}
      defaultSort={defaultSort}
      filter={false}
      headThemeLight
      numOfColumns={numOfColumns}
      rows={filterCanceledUsers(users)}
      sort
    />
  )
}

SiteUserTrainingGrid.propTypes = {
  className: PropTypes.string,
  users: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.oneOf([VARIANT_K8_CORE, VARIANT_K12_DWB]),
}

SiteUserTrainingGrid.defaultProps = {
  variant: VARIANT_K8_CORE,
}

export default SiteUserTrainingGrid
